import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { useTranslation } from "@refinedev/core";
import Button from "components/button";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import { TooltipParagraph } from "components/tooltipParagraph";
import {
  ApplyToProductType,
  DiscountType,
  IPromotionCouponItem,
  UserType,
} from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  canAccessByType,
  formatNumber,
  getApplyToProductTypeLabel,
  getFormattedDateTime,
  isHaveActionPermission,
} from "utills/helpers";

interface PromotionCouponTableProps {
  data?: IPromotionCouponItem[];
  pageSize: number;
  currentPage: number;
  _toggleActive: (id: string, isActive: boolean, conditionName: string) => void;
  _showInfoModal: (data: IPromotionCouponItem) => void;
}

export const PromotionCouponTable = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
  _toggleActive,
  _showInfoModal,
}: PromotionCouponTableProps) => {
  const { translate } = useTranslation();
  return (
    <div className="flow-root">
      <div className="relative inline-block w-full align-middle box-border">
        <table className="w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
          <thead>
            <tr>
              <th scope="col" className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">
                {translate("no")}
              </th>
              <th scope="col" className="th">
                {translate("coupon-code")}
              </th>
              <th scope="col" className="th">
                {translate("condition-name")}
              </th>
              {canAccessByType(UserType.ADMIN) && (
                <th scope="col" className="th">
                  Apply For
                </th>
              )}
              <th scope="col" className="th">
                {translate("discount-condition")}
              </th>
              <th scope="col" className="th">
                {translate("used-count-per-total")}
              </th>
              <th scope="col" className="th">
                {translate("apply-from-to")}
              </th>
              {isHaveActionPermission("coupon", "promotion", "edit") && (
                <th scope="col" className="th">
                  {translate("status")}
                </th>
              )}
              <th scope="col" className="th w-28">
                <span className="sr-only">Action</span>
                <p className="">{translate("action")}</p>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((item: IPromotionCouponItem, index: number) => (
              <tr key={index}>
                <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                  {(currentPage - 1) * pageSize + 1 + index}
                </td>
                <td className="td font-medium">{item?.code}</td>
                <td className="td max-w-20 overflow-x-auto">
                  <Link
                    to={`/promotion/condition/${item.rule_id}`}
                    className="whitespace-pre-wrap max-w-[280px] text-brand-500 font-medium hover:underline"
                  >
                    <p>{item.note}</p>
                  </Link>
                </td>
                {canAccessByType(UserType.ADMIN) && (
                  <td className="td">
                    <div className="flex flex-wrap items-center gap-2">
                      <Tag
                        text={getApplyToProductTypeLabel(
                          item.apply_to_product_type
                        )}
                      />
                      {!isEmpty(item?.event_ids) &&
                      item?.apply_to_product_type !== ApplyToProductType.ALL ? (
                        <div
                          className="hover:cursor-pointer"
                          onClick={() => _showInfoModal(item)}
                        >
                          <Tag
                            text={`${(item.event_ids ?? []).length} event(s)`}
                            customClassName="bg-gray-100 text-gray-500 underline"
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </td>
                )}
                <td className="td">
                  {item?.discount_type === DiscountType.PERCENT ? (
                    item?.discount_value ? (
                      <p>{formatNumber(item?.discount_value ?? 0)}%</p>
                    ) : (
                      <p className="text-gray-400">N/A</p>
                    )
                  ) : item?.discount_type === DiscountType.FIX_PRICE ? (
                    <>
                      {item?.discount_value || item?.discount_value_usd ? (
                        <p>
                          {item?.discount_value
                            ? `${formatNumber(item?.discount_value)}đ`
                            : ""}{" "}
                          {item?.discount_value && item?.discount_value_usd
                            ? "|"
                            : ""}{" "}
                          {item?.discount_value_usd
                            ? `$${formatNumber(item?.discount_value_usd)}`
                            : ""}
                        </p>
                      ) : (
                        <p className="text-gray-400">N/A</p>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </td>
                <td className="td">
                  <span className="text-xs border border-solid border-brand-500 rounded-full px-3 py-1 text-brand-500">
                    {item.times_used}/{item.max_times_used}
                  </span>
                </td>
                <td className="td">
                  {getFormattedDateTime(item.apply_from)} -<br />{" "}
                  {getFormattedDateTime(item.apply_to)}
                </td>
                {isHaveActionPermission("coupon", "promotion", "edit") && (
                  <td className="td">
                    <SwitchButton
                      checked={item.is_active}
                      onChange={() =>
                        _toggleActive(item.id, !item.is_active, item.code)
                      }
                    />
                  </td>
                )}
                <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                  <div className="flex justify-start items-center gap-x-2">
                    <Link to={`/promotion/coupon/${item.id}`} className="w-fit">
                      <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                        <EyeIcon className="w-4 h-4 text-black" />
                      </Button>
                    </Link>
                    {isHaveActionPermission("coupon", "promotion", "edit") ? (
                      <Link
                        to={`/promotion/coupon/${item.id}/edit`}
                        className="w-fit"
                      >
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : (
                      <></>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
